import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download } from 'lucide-react';
import publications from '../data/publicationsData';
import ContactModal from '../components/ContactModal';
import SEO from '../components/SEO';

const PublicationDetail = () => {
  const { slug } = useParams();
  const pub = publications.find(p => p.id === slug);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!pub) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Publication Not Found</h1>
          <Link to="/resources/publications" className="text-blue-600 font-semibold hover:underline">Back to Publications</Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('## ')) return <h2 key={i} className="text-2xl font-bold text-slate-900 mt-10 mb-4">{trimmed.replace('## ', '')}</h2>;
      if (trimmed.startsWith('### ')) return <h3 key={i} className="text-xl font-bold text-slate-800 mt-6 mb-3">{trimmed.replace('### ', '')}</h3>;
      if (trimmed.startsWith('- ')) return <li key={i} className="text-slate-600 leading-relaxed ml-6 mb-2 list-disc">{trimmed.replace('- ', '')}</li>;
      return <p key={i} className="text-slate-600 leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="publication-detail-page">
      <SEO title={pub.title} description={pub.excerpt || pub.title} path={`/resources/publications/${pub.id}`} />
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/resources/publications" className="inline-flex items-center text-blue-300 hover:text-white mb-6 transition-colors" data-testid="back-to-publications">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Publications
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <span className="bg-blue-500/30 text-blue-200 text-sm font-semibold px-4 py-1.5 rounded-full border border-blue-400/30">{pub.type}</span>
            <span className="flex items-center gap-1 text-blue-300 text-sm"><FileText className="h-4 w-4" /> {pub.pages}</span>
            <span className="text-blue-300 text-sm">{pub.size}</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight">{pub.title}</h1>
          <p className="text-xl text-blue-100">{pub.description}</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
        <img src={pub.image} alt={pub.title} className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl" />
      </div>

      <article className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderContent(pub.content)}
        </div>
      </article>

      {/* CTA */}
      <section className="bg-slate-50 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Need Personalized Guidance?</h2>
          <p className="text-slate-600 mb-6">Talk to our trademark experts for customized advice on protecting your brand.</p>
          <button
            onClick={() => setIsModalOpen(true)}
            data-testid="publication-cta-button"
            className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-xl shadow-lg transition-all duration-300"
          >
            Schedule Free Consultation
          </button>
        </div>
      </section>

      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Schedule Consultation" />
    </div>
  );
};

export default PublicationDetail;
